/**
 * Makes a DevExtreme DataGrid as tall as its rows, capped at a maximum height (it scrolls
 * internally beyond that), so controls placed after the grid follow it directly.
 *
 * DevExtreme 22.1 cannot do this itself: with CSS max-height the grid stays at the maximum
 * height once reached, even after filtering down to a few rows. The grid must not also receive
 * a `height` prop, because devextreme-react would roll back the height set here.
 *
 * Call from the grid's onInitialized handler.
 */
const MIN_MAX_HEIGHT = 200;

export function attachGridAutoHeight(component: any, getMaxHeight: () => number): void {
  if (!component || typeof window === "undefined") return;

  let frame = 0;
  let observedTable: Element | null = null;
  const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => schedule()) : null;
  const maxHeight = () => Math.max(MIN_MAX_HEIGHT, Math.round(getMaxHeight()));

  const fit = () => {
    frame = 0;
    const element = component.element?.();
    const root: HTMLElement | undefined = element?.get ? element.get(0) : element;
    // Skip while detached or hidden (display: none); the ResizeObserver re-fits once visible.
    if (!root || !root.isConnected || root.offsetParent === null) return;

    const rowsView = root.querySelector<HTMLElement>(".dx-datagrid-rowsview");
    const table = rowsView?.querySelector<HTMLTableElement>(
      ".dx-datagrid-content:not(.dx-datagrid-content-fixed) > table.dx-datagrid-table",
    );
    if (!rowsView || !table) return;

    if (observer && table !== observedTable) {
      observer.disconnect();
      observer.observe(table);
      observedTable = table;
    }

    // The free-space row is DevExtreme's filler for unused height; it is not content.
    const freeSpaceRow = Array.from(table.rows).find((row) => row.classList.contains("dx-freespace-row"));
    const rowsHeight = table.offsetHeight - (freeSpaceRow?.offsetHeight ?? 0);
    const scroller = rowsView.querySelector<HTMLElement>(".dx-scrollable-container");
    const horizontalScrollbar = scroller ? Math.max(0, scroller.offsetHeight - scroller.clientHeight) : 0;
    const emptyMinHeight = parseFloat(getComputedStyle(rowsView).minHeight) || 0;
    const chrome = root.offsetHeight - rowsView.offsetHeight;

    const next = Math.round(Math.min(chrome + Math.max(rowsHeight + horizontalScrollbar, emptyMinHeight), maxHeight()));
    const current = component.option("height");
    if (typeof current === "number" && Math.abs(current - next) <= 1) return;
    component.option("height", next);
  };

  function schedule() {
    if (!frame) frame = window.requestAnimationFrame(fit);
  }

  const dispose = () => {
    if (frame) window.cancelAnimationFrame(frame);
    observer?.disconnect();
    window.removeEventListener("resize", schedule);
  };

  // Start at the maximum height so a large data set never renders at full length first.
  component.option("height", maxHeight());
  component.on("contentReady", schedule);
  component.on("disposing", dispose);
  window.addEventListener("resize", schedule);
}
